import React, { useEffect, useState } from 'react';
import {
  getSnackBar,
  useNotification,
  getButtonSpinner,
  pageSizeChangeHandler,
} from 'utils/commonUtils';
import {
  LabelValue,
  getCurrentWeek,
  formatDateString,
  SsaStudentProfileLoader,
} from '../../ssaCommonUtils';
import {
  InComplianceGrid,
  AttendanceSessionGrid,
} from '../../ssaComponentStyled';
import { format } from 'date-fns';
import MDBox from 'components/MDBox';
import Grid from '@mui/material/Grid';
import { useForm } from 'react-hook-form';
import { intialPageSize } from 'constants';
import {
  getStudentDetails,
  getStudentCourseData,
} from 'layouts/students/service/studentsApi';
import { GridForm } from 'components/Ui/styled';
import MDTypography from 'components/MDTypography';
import { EmptyState } from './SsaAtendanceTabUtils';
import { useSelector, useDispatch } from 'react-redux';
import CustomizedSnackbars from 'components/snackbar/Snackbar';
import DeltaDataGrid from 'utils/CommonDataGrid/CommonDataGrid';
import PreviousWeekNoteHistory from './PreviousWeekNoteHistory';
import {
  getAttendanceWeeksOptions,
  postStudentAttendanceNote,
} from 'layouts/applications/attendance/services/attendanceApis';
import { attendanceCourseColumns } from './attendanceCourseColumns';
import { FormInputText } from 'components/Common/Forms/FormTextField';
import AssetNotes from 'layouts/applications/attendance/AttendanceNotes';
import { FormInputSwitch } from 'components/Common/Forms/FormInputSwitch';
import FormInputDropdown from 'components/Common/Forms/FormInputDropDown';
import { displaySnackbar } from 'components/snackbar/store/SnackbarSlice';
import {
  getCourseChecks,
  postCourseChecks,
  deleteCourseCheck,
} from 'layouts/studentSuccessAdvocate/FilterPresets/services/courseChecksApi';
import { attendanceStatusOptions } from 'layouts/applications/attendance/common/constants';
import { setSelectedSsaStudentDetails } from 'layouts/studentSuccessAdvocate/ssa/store/ssaStudentDetailSlice/ssaStudentDetailSlice';
import { setSidebarSelectedStudent } from 'layouts/studentSuccessAdvocate/ssa/store/ssaSidenavBarStudentDetails/ssaSidenavBarStudentDetailsSlice';

const SsaAttendanceTab = ({ onError, onErrorMessage }) => {
  const selectedId = useSelector(
    (state) => state?.ssaStudentDetail?.selectedSsaStudent || ''
  );
  const selectedSsaStudentDetails = useSelector(
    (state) => state?.ssaStudentDetail?.selectedSsaStudentDetails || {}
  );
  const ssaStudentDetail = useSelector(
    (state) => state?.ssaStudentDetail?.selectedStudentHeadingDetails || {}
  );
  const sidebarStudents = useSelector(
    (state) => state?.ssaSidenavBarStudentDetails?.sideBarSelectedStudent || []
  );

  const { rawProgressData } = selectedSsaStudentDetails || {};
  const { dataFrom: CachedFromDate = '' } = rawProgressData || {};

  const dispatch = useDispatch();
  const { NotificationPopup, handleErrorResponse } = useNotification();

  const [notesData, setNotesData] = useState(null);
  const [weekOptions, setWeekOptions] = useState([]);
  const [liveCourseData, setLiveCourseData] = useState({});
  const [selectedCourseIds, setSelectedCourseIds] = useState([]);
  const [studentProfileDetail, setStudentProfileDetail] = useState({});
  const [updateButtonLoading, setUpdateButtonLoading] = useState(false);
  const [attendanceTabLoading, setAttendanceTabLoading] = useState(false);
  const [courseListData, setCourseListData] = useState({
    rows: [],
    pageSize: intialPageSize,
    isLoading: false,
  });

  useEffect(() => {
    if (selectedId) {
      fetchStudentAttendance();
    }
  }, [selectedId]);

  const fetchStudentAttendance = async () => {
    try {
      setAttendanceTabLoading(true);
      const getStudentProfileDetails = await getStudentDetails(selectedId);
      const item = getStudentProfileDetails?.item;

      setStudentProfileDetail(item);
      dispatch(setSelectedSsaStudentDetails(item));

      // Attendance notes (existing saved weeks)
      const attendanceNotes = item?.attendanceData?.notes || [];
      const weeks = attendanceNotes.map((note) => ({
        id: note?.week,
        value: note?.week,
        label: note?.week,
      }));

      const allWeeksResponse = await getAttendanceWeeksOptions(
        ssaStudentDetail?.semester
      );

      const allWeeks = allWeeksResponse.map((w) => ({
        id: w?.descr,
        value: w?.descr,
        label: w?.descr,
      }));
      setWeekOptions(allWeeks);

      // ✅ Always select latest week (index 0)
      const currentWeek = getCurrentWeek(allWeeksResponse);

      if (currentWeek) {
        setValue('week', currentWeek?.descr);
      } else if (allWeeksResponse?.length > 0) {
        setValue('week', allWeeksResponse[allWeeksResponse.length - 1].descr);
      }

      const cleanCourseName = (name = '') => name.split(' - ')[0].trim();

      const rows =
        item?.attendanceData?.courseInfo?.map((course, index) => ({
          id: index,
          courseName: cleanCourseName(course?.courseName),
          actualGrade: course?.actualGrade,
          lastOnlineActivity: course?.lastOnlineActivity
            ? format(new Date(course?.lastOnlineActivity), 'MM-dd-yyyy hh:mm a')
            : '',
          numSchoolDaysSinceOnline: course?.numSchoolDaysSinceOnline,
        })) || [];

      setCourseListData((prev) => ({
        ...prev,
        rows,
      }));

      // if (weeks?.length > 0) setValue('week', weeks[0]?.id);

      setAttendanceTabLoading(false);
      onError(false);
      onErrorMessage('');
    } catch (error) {
      const msg =
        error?.response?.data?.message || 'Failed to load attendance.';
      onError(true);
      onErrorMessage(msg); // <-- SEND MESSAGE TO PARENT
      setAttendanceTabLoading(false);
      handleErrorResponse(msg);
    }
  };

  const defaultValue = {
    week: '',
    note: '',
    label: '',
    inCompliance: false,
    fetchLiveData: false,
  };

  const useFunction = useForm({
    mode: 'onChange',
    defaultValues: defaultValue,
  });

  const { handleSubmit, control, formState, setValue, watch, reset } =
    useFunction;
  const { errors } = formState;

  const watchedWeek = watch('week');
  const watchedFetchLiveData = watch('fetchLiveData');

  useEffect(() => {
    if (selectedId) {
      reset(defaultValue);
      setNotesData(null);
    }
  }, [selectedId, reset]);

  useEffect(() => {
    if (
      selectedId &&
      watchedWeek &&
      ssaStudentDetail?.semester &&
      courseListData?.rows?.length > 0
    ) {
      fetchStudentCourseChecks();
    }
  }, [
    selectedId,
    watchedWeek,
    courseListData?.rows, // ✅ RUN AGAIN when rows are available
    ssaStudentDetail?.semester,
  ]);

  useEffect(() => {
    if (watchedFetchLiveData && selectedId) {
      getStudentLiveCourseData();
    }
  }, [watchedFetchLiveData, selectedId]);

  const fetchStudentCourseChecks = async () => {
    setCourseListData((prev) => ({ ...prev, isLoading: true }));
    try {
      const result = await getCourseChecks(
        selectedId,
        watchedWeek,
        ssaStudentDetail?.semester
      );

      const checkedCourseNames = Object.keys(result?.checkedCourses || {});

      // Match names and get row ids from DataGrid rows
      const matchedIds = courseListData.rows
        .filter((row) =>
          checkedCourseNames.some((name) => row.courseName.includes(name))
        )
        .map((row) => row.id);

      setSelectedCourseIds(matchedIds);
      setCourseListData((prev) => ({ ...prev, isLoading: false }));
    } catch (error) {
      setCourseListData((prev) => ({ ...prev, isLoading: false }));
      handleErrorResponse(error?.response?.data?.message);
    }
  };

  // Update form when week is selected
  useEffect(() => {
    if (watchedWeek && studentProfileDetail?.attendanceData?.notes) {
      const weekData = studentProfileDetail?.attendanceData?.notes.find(
        (note) => note?.week === watchedWeek
      );

      if (weekData) {
        setValue('note', weekData?.note);
        setValue('label', weekData?.label);
        setValue('inCompliance', weekData?.inCompliance);
        setNotesData(weekData);
      }
    }
  }, [watchedWeek, studentProfileDetail, setValue]);

  const handleUpdateWeekNote = async (data) => {
    const snackBar = getSnackBar('Successfully attendance note is saved');
    const { note, week, label, inCompliance } = data || {};
    const payload = {
      note: note ?? '',
      week: week ?? '',
      label: label ?? '',
      inCompliance: inCompliance ?? false,
      semester: ssaStudentDetail?.semester,
    };
    try {
      setUpdateButtonLoading(true);
      await postStudentAttendanceNote(payload, selectedId);
      dispatch(displaySnackbar(snackBar));
      await fetchStudentAttendance();

      const updatedSidebarStudents = sidebarStudents?.map((student) =>
        student?.id === selectedId
          ? { ...student, inCompliance: inCompliance }
          : student
      );
      dispatch(setSidebarSelectedStudent(updatedSidebarStudents));

      setUpdateButtonLoading(false);
    } catch (error) {
      setUpdateButtonLoading(false);
      handleErrorResponse(error?.response?.data?.message);
    }
  };

  const handleToggleSelect = async (id, checked) => {
    const selectedRow = courseListData?.rows?.find((row) => row?.id === id);
    if (!selectedRow) return;

    const payload = {
      courseName: selectedRow?.courseName,
      week: watchedWeek,
      semester: ssaStudentDetail?.semester,
    };

    try {
      if (checked) {
        setCourseListData((prev) => ({ ...prev, isLoading: true }));
        await postCourseChecks(selectedId, payload);

        setSelectedCourseIds((prev) => [...prev, id]);
        setCourseListData((prev) => ({ ...prev, isLoading: false }));
      } else {
        setCourseListData((prev) => ({ ...prev, isLoading: true }));
        await deleteCourseCheck(
          selectedId,
          selectedRow.courseName,
          watchedWeek,
          ssaStudentDetail?.semester
        );

        setSelectedCourseIds((prev) => prev.filter((x) => x !== id));
        setCourseListData((prev) => ({ ...prev, isLoading: false }));
      }
    } catch (error) {
      handleErrorResponse(error?.response?.data?.message);
    }
  };

  const getStudentLiveCourseData = async () => {
    try {
      setCourseListData((prev) => ({ ...prev, isLoading: true }));
      const getLiveCourseData = await getStudentCourseData(selectedId);
      setLiveCourseData(getLiveCourseData);
      setCourseListData((prev) => ({ ...prev, isLoading: false }));
    } catch (error) {
      setCourseListData((prev) => ({ ...prev, isLoading: false }));
      handleErrorResponse(error?.response?.data?.message);
    }
  };

  const pageSizeChange = pageSizeChangeHandler(setCourseListData);

  // Prevent premature render before student data is ready
  if (!selectedId || !selectedSsaStudentDetails?.studentId) {
    return <SsaStudentProfileLoader />;
  }

  if (attendanceTabLoading) {
    return <SsaStudentProfileLoader />;
  }

  const providersList = Array.isArray(liveCourseData.providersQueried)
    ? liveCourseData.providersQueried.join(', ')
    : '-';

  const liveFetchedDate = formatDateString(liveCourseData?.fetchedAt);
  const isCachedFromDate = formatDateString(CachedFromDate);
  return (
    <>
      <CustomizedSnackbars />
      <GridForm container spacing={2}>
        {/* Left Section */}
        <AttendanceSessionGrid item xs={12} md={6}>
          <MDTypography
            variant="button"
            fontWeight="bold"
            textTransform="capitalize"
          >
            Semester: {ssaStudentDetail?.semester} | Week : {watchedWeek}
          </MDTypography>

          <Grid container spacing={2} mt={1}>
            {/* <Grid item xs={12}>
              <FormInputDropdown
                name="week"
                disabled={true}
                control={control}
                label="Select Week"
                options={weekOptions}
                errors={!!errors.week}
                helperText={errors?.week?.message}
              />
            </Grid> */}

            <Grid item xs={12}>
              <FormInputDropdown
                name="label"
                control={control}
                errors={!!errors.label}
                label="Attendance Status"
                options={attendanceStatusOptions}
                helperText={errors?.label?.message}
              />
            </Grid>
            <InComplianceGrid item xs={12}>
              <FormInputSwitch
                control={control}
                name="inCompliance"
                label="In Compliance"
              />
            </InComplianceGrid>
            <Grid item xs={12}>
              <FormInputText
                rows={3}
                multiline
                name="note"
                control={control}
                errors={!!errors.note}
                label="Attendance Note"
                helperText={errors?.note?.message}
              />
            </Grid>

            <Grid item xs={12}></Grid>
            <Grid item xs={12} md={12} justifyItems="right">
              {getButtonSpinner(
                updateButtonLoading,
                handleSubmit(handleUpdateWeekNote),
                'Update Attendance Note'
              )}
            </Grid>

            <Grid item xs={12}>
              {notesData ? (
                <AssetNotes heading="Note History" notesData={notesData} />
              ) : (
                <EmptyState message="ℹ️ No existing note for this week" />
              )}
            </Grid>
          </Grid>
        </AttendanceSessionGrid>

        {/* Right Section */}
        <AttendanceSessionGrid item md={6} xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <FormInputSwitch
                control={control}
                name="fetchLiveData"
                label="Fetch Live Data"
              />
            </Grid>
            <Grid item xs={12}>
              <LabelValue label="Cached from" value={isCachedFromDate} />

              {watchedFetchLiveData && liveCourseData && (
                <>
                  &nbsp;|&nbsp;
                  <LabelValue label="Live Fetched" value={liveFetchedDate} />
                  &nbsp;|&nbsp;
                  <LabelValue label="Providers" value={providersList} />
                </>
              )}
            </Grid>

            <Grid item xs={12}>
              <DeltaDataGrid
                listData={courseListData}
                columns={attendanceCourseColumns(
                  selectedCourseIds,
                  handleToggleSelect
                )}
                setListData={pageSizeChange}
                defaultSortField="courseName"
              />
            </Grid>
          </Grid>
        </AttendanceSessionGrid>

        <MDBox mb={4} />

        <Grid item xs={12}>
          <PreviousWeekNoteHistory
            heading="Previous Week Note History"
            notesData={studentProfileDetail?.attendanceData?.notes}
          />
        </Grid>
      </GridForm>
      <NotificationPopup />
    </>
  );
};

export default SsaAttendanceTab;
